import { CircleCheck, Coins, Plus, Star, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { deleteExperience } from '@/data/experiencesRepo';
import { useCollections } from '@/hooks/useCollections';
import { useExperiences } from '@/hooks/useExperiences';
import {
  experienceRegionLabels,
  experienceStatusLabels,
  type ExperienceRegionType,
  type ExperienceStatusType,
  type ExperienceType,
} from '@/types/types_experience';
import { formatPrice } from '@/util/util_format';

type RegionFilter = ExperienceRegionType | 'all';
type StatusFilter = ExperienceStatusType | 'all';
type CollectionFilter = 'all' | 'none' | string;

const statusPillClass: Record<ExperienceStatusType, string> = {
  active: 'tw:bg-green-100 tw:text-green-800',
  inactive: 'tw:bg-gray-100 tw:text-gray-800',
  'coming-soon': 'tw:bg-yellow-100 tw:text-yellow-800',
};

/**
 * Admin list of experiences, laid out like the original static admin.html
 * "Experience Database" grid: a filter bar above a card grid, with experiences
 * grouped under their collection.
 */
export function PageExperienceList() {
  const { experiences, loading } = useExperiences();
  const { collections } = useCollections();
  const [toDelete, setToDelete] = useState<ExperienceType | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [region, setRegion] = useState<RegionFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [collectionFilter, setCollectionFilter] = useState<CollectionFilter>('all');

  const filtered = useMemo(
    () =>
      experiences.filter(
        (e) =>
          (region === 'all' || e.region === region) &&
          (status === 'all' || e.status === status) &&
          (collectionFilter === 'all' ||
            (collectionFilter === 'none' ? e.collectionId === null : e.collectionId === collectionFilter)),
      ),
    [experiences, region, status, collectionFilter],
  );

  // Group by collection (in collection order), with standalone experiences last —
  // mirrors the parent/child grouping in the original static admin's grid.
  const { collectionGroups, standalone } = useMemo(() => {
    const byCollection = new Map<string, ExperienceType[]>();
    const standaloneItems: ExperienceType[] = [];
    for (const e of filtered) {
      if (e.collectionId) {
        const list = byCollection.get(e.collectionId) ?? [];
        list.push(e);
        byCollection.set(e.collectionId, list);
      } else {
        standaloneItems.push(e);
      }
    }
    const groups = collections
      .filter((c) => byCollection.has(c.id))
      .map((c) => ({ id: c.id, name: c.name, items: byCollection.get(c.id)! }));
    return { collectionGroups: groups, standalone: standaloneItems };
  }, [filtered, collections]);

  async function confirmDelete() {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteExperience(toDelete.id);
      setToDelete(null);
      // The snapshot listener removes the row automatically.
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <header className='tw:mb-6 tw:flex tw:items-center tw:justify-between'>
        <div>
          <h1 className='tw:font-serif tw:text-2xl tw:text-ed-black'>Experience Database</h1>
          <p className='tw:mt-1 tw:text-sm tw:text-ed-stone'>
            Manage all experiences. Changes reflect automatically on the catalog page.
          </p>
        </div>
        <Link
          to='/admin/experiences/new'
          className='tw:flex tw:items-center tw:gap-2 tw:bg-ed-gold tw:px-4 tw:py-2 tw:text-sm tw:font-medium tw:text-ed-black tw:transition-colors tw:hover:bg-ed-black tw:hover:text-ed-cream'
        >
          <Plus className='tw:size-4' /> Add New Experience
        </Link>
      </header>

      <div className='tw:mb-8 tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:shadow-sm'>
        <div className='tw:flex tw:flex-wrap tw:gap-4 tw:border-b tw:border-ed-umber/10 tw:p-4'>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value as RegionFilter)}
            className='tw:rounded-sm tw:border tw:border-ed-umber/20 tw:px-4 tw:py-2 tw:text-sm tw:focus:border-ed-gold tw:focus:outline-none'
          >
            <option value='all'>All Regions</option>
            {(Object.entries(experienceRegionLabels) as [ExperienceRegionType, string][]).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
          <select
            value={collectionFilter}
            onChange={(e) => setCollectionFilter(e.target.value)}
            className='tw:rounded-sm tw:border tw:border-ed-umber/20 tw:px-4 tw:py-2 tw:text-sm tw:focus:border-ed-gold tw:focus:outline-none'
          >
            <option value='all'>All Collections</option>
            <option value='none'>Standalone</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
            className='tw:rounded-sm tw:border tw:border-ed-umber/20 tw:px-4 tw:py-2 tw:text-sm tw:focus:border-ed-gold tw:focus:outline-none'
          >
            <option value='all'>All Status</option>
            {(Object.entries(experienceStatusLabels) as [ExperienceStatusType, string][]).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className='tw:p-6'>
          {loading ? (
            <p className='tw:py-10 tw:text-center tw:text-ed-stone'>Loading…</p>
          ) : filtered.length === 0 ? (
            <p className='tw:py-10 tw:text-center tw:text-ed-stone'>No experiences match these filters.</p>
          ) : (
            <div className='tw:space-y-8'>
              {collectionGroups.map((group) => (
                <div key={group.id} className='tw:rounded-sm tw:bg-ed-cream/30 tw:p-6'>
                  <h2 className='tw:mb-4 tw:flex tw:items-center tw:gap-2 tw:font-serif tw:text-lg tw:text-ed-black'>
                    <span className='tw:size-2 tw:rounded-full tw:bg-ed-gold' />
                    {group.name}
                  </h2>
                  <div className='tw:grid tw:grid-cols-1 tw:gap-6 tw:md:grid-cols-2 tw:lg:grid-cols-4 tw:xl:grid-cols-5'>
                    {group.items.map((experience) => (
                      <ExperienceAdminCard key={experience.id} experience={experience} onDelete={setToDelete} />
                    ))}
                  </div>
                </div>
              ))}

              {standalone.length > 0 && (
                <div>
                  {collectionGroups.length > 0 && (
                    <h2 className='tw:mb-4 tw:font-serif tw:text-lg tw:text-ed-black'>Standalone Experiences</h2>
                  )}
                  <div className='tw:grid tw:grid-cols-1 tw:gap-6 tw:md:grid-cols-2 tw:lg:grid-cols-4 tw:xl:grid-cols-5'>
                    {standalone.map((experience) => (
                      <ExperienceAdminCard key={experience.id} experience={experience} onDelete={setToDelete} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!toDelete} onOpenChange={(open) => !open && !deleting && setToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete experience?</DialogTitle>
            <DialogDescription>
              This will permanently remove &ldquo;{toDelete?.name}&rdquo;. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' disabled={deleting} onClick={() => setToDelete(null)}>
              Cancel
            </Button>
            <Button variant='destructive' loading={deleting} onClick={() => void confirmDelete()}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExperienceAdminCard({
  experience,
  onDelete,
}: {
  experience: ExperienceType;
  onDelete: (experience: ExperienceType) => void;
}) {
  return (
    <div className='tw:flex tw:h-full tw:flex-col tw:overflow-hidden tw:rounded-sm tw:border tw:border-ed-umber/10 tw:bg-white tw:shadow-sm tw:transition-shadow tw:hover:shadow-md'>
      <div className='tw:relative tw:h-40 tw:shrink-0 tw:bg-ed-umber/10'>
        <img src={experience.heroImage} alt={experience.name} className='tw:h-full tw:w-full tw:object-cover' />
        <div className='tw:absolute tw:top-2 tw:right-2'>
          <span
            className={`tw:rounded-full tw:px-2 tw:py-1 tw:text-xs ${statusPillClass[experience.status]}`}
          >
            {experienceStatusLabels[experience.status]}
          </span>
        </div>
        {typeof experience.featuredOrder === 'number' && (
          <div className='tw:absolute tw:top-2 tw:left-2'>
            <span className='tw:inline-flex tw:items-center tw:gap-1 tw:rounded-full tw:bg-ed-gold tw:px-2 tw:py-1 tw:text-xs tw:text-ed-black'>
              <Star className='tw:size-3 tw:fill-current' /> Featured #{experience.featuredOrder}
            </span>
          </div>
        )}
      </div>
      <div className='tw:flex tw:flex-1 tw:flex-col tw:p-4'>
        <p className='tw:mb-1 tw:text-xs tw:tracking-wider tw:text-ed-gold tw:uppercase'>
          {experienceRegionLabels[experience.region]}
        </p>
        <h3 className='tw:mb-1 tw:font-serif tw:text-lg tw:text-ed-black'>{experience.name}</h3>
        <p className='tw:mb-3 tw:text-sm tw:text-ed-stone'>{experience.subtitle}</p>
        <div className='tw:mb-3 tw:flex tw:items-center tw:justify-between tw:text-sm'>
          <span className='tw:text-ed-charcoal/70'>{experience.venueName}</span>
          <span className='tw:font-medium tw:text-ed-black'>{formatPrice(experience.basePrice)}</span>
        </div>
        <div className='tw:mb-3 tw:flex tw:flex-wrap tw:gap-3'>
          {experience.commissionEnabled && (
            <span className='tw:inline-flex tw:items-center tw:gap-1 tw:text-xs tw:text-ed-gold'>
              <Coins className='tw:size-3' /> {experience.commissionPercent}% commission
            </span>
          )}
          {experience.famEnabled && (
            <span className='tw:inline-flex tw:items-center tw:gap-1 tw:text-xs tw:text-ed-green'>
              <CircleCheck className='tw:size-3' /> FAM available
            </span>
          )}
        </div>
        <div className='tw:mt-auto tw:flex tw:gap-2 tw:pt-1'>
          <Link
            to={`/admin/experiences/${experience.id}/edit`}
            className='tw:flex-1 tw:border tw:border-ed-umber/30 tw:py-2 tw:text-center tw:text-sm tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-gold tw:hover:text-ed-gold'
          >
            Edit
          </Link>
          <Link
            to={`/experiences/${experience.id}`}
            target='_blank'
            className='tw:flex-1 tw:bg-ed-gold tw:py-2 tw:text-center tw:text-sm tw:text-ed-black tw:transition-colors tw:hover:bg-ed-black tw:hover:text-ed-cream'
          >
            View
          </Link>
          <button
            type='button'
            title='Delete'
            onClick={() => onDelete(experience)}
            className='tw:border tw:border-ed-umber/30 tw:px-3 tw:text-ed-charcoal tw:transition-colors tw:hover:border-ed-red tw:hover:text-ed-red'
          >
            <Trash2 className='tw:size-4' />
          </button>
        </div>
      </div>
    </div>
  );
}
